import { Text } from "@mantine/core";
import type { ComponentType } from "react";

import { RuleEngineAdvancementRuleFiredPayload } from "./payloads/RuleEngineAdvancementRuleFiredPayload";
import { RuleEngineCrossStepTransferredPayload } from "./payloads/RuleEngineCrossStepTransferredPayload";
import { RuleEngineEffectAdvancePayload } from "./payloads/RuleEngineEffectAdvancePayload";
import { RuleEngineEffectCreateGroupPayload } from "./payloads/RuleEngineEffectCreateGroupPayload";
import { RuleEngineEffectEliminatePayload } from "./payloads/RuleEngineEffectEliminatePayload";
import { RuleEngineEffectEmitSeedingPinPayload } from "./payloads/RuleEngineEffectEmitSeedingPinPayload";
import { RuleEngineEffectEndStepPayload } from "./payloads/RuleEngineEffectEndStepPayload";
import { RuleEngineEffectSetMetadataPayload } from "./payloads/RuleEngineEffectSetMetadataPayload";
import { RuleEngineGenerationScriptRanPayload } from "./payloads/RuleEngineGenerationScriptRanPayload";
import { RuleEngineManualOverridePayload } from "./payloads/RuleEngineManualOverridePayload";
import { TournamentsStepGeneratedPayload } from "./payloads/TournamentsStepGeneratedPayload";
import { TournamentsStepResetPayload } from "./payloads/TournamentsStepResetPayload";

/**
 * Concrete `__typename` values for every `JournalPayload` implementation
 * in the GraphQL schema. Used as the literal-string union key of the
 * dispatch registry below — adding a new payload type is a one-line edit
 * to this union AND the registry, and TypeScript enforces exhaustiveness.
 */
type JournalPayloadTypename =
  | "RuleEngineAdvancementRuleFiredPayload"
  | "RuleEngineCrossStepTransferredPayload"
  | "RuleEngineEffectAdvancePayload"
  | "RuleEngineEffectCreateGroupPayload"
  | "RuleEngineEffectEliminatePayload"
  | "RuleEngineEffectEmitSeedingPinPayload"
  | "RuleEngineEffectEndStepPayload"
  | "RuleEngineEffectSetMetadataPayload"
  | "RuleEngineGenerationScriptRanPayload"
  | "RuleEngineManualOverridePayload"
  | "TournamentsStepGeneratedPayload"
  | "TournamentsStepResetPayload";

/**
 * Discriminator for journal payloads. The `JournalPayload` interface in
 * GraphQL is implemented by 12 concrete object types, each rendered by a
 * dedicated component co-located with its fragment.
 *
 * Dispatch is configuration-driven: each entry of {@link PAYLOAD_RENDERERS}
 * maps a `__typename` to its renderer. Adding a new payload type is a
 * one-line registry edit (Open/Closed), not a `switch` modification.
 *
 * The single contained `as unknown as` boundary lives inside the registry
 * — call sites of {@link JournalPayloadRenderer} never assert anything,
 * and each renderer receives the structurally compatible fragment shape
 * it declares in its own `Props`.
 */
interface PayloadWithTypename {
  __typename: string;
}

interface Props {
  payload: PayloadWithTypename;
}

type PayloadRenderer = ComponentType<{ data: PayloadWithTypename }>;

/**
 * `Record<JournalPayloadTypename, PayloadRenderer>` makes the registry
 * exhaustive: omitting any concrete payload typename is a compile error.
 *
 * Each component is declared with its own typed `Props`; we erase that
 * specificity here through a single `as unknown as` so all 12 entries
 * share one map shape. The runtime guarantee is provided by the schema
 * — the server only emits a payload whose `__typename` matches the
 * fragment's `on` clause, so the lookup is sound at the boundary.
 */
const PAYLOAD_RENDERERS: Record<JournalPayloadTypename, PayloadRenderer> = {
  RuleEngineAdvancementRuleFiredPayload:
    RuleEngineAdvancementRuleFiredPayload as unknown as PayloadRenderer,
  RuleEngineCrossStepTransferredPayload:
    RuleEngineCrossStepTransferredPayload as unknown as PayloadRenderer,
  RuleEngineEffectAdvancePayload:
    RuleEngineEffectAdvancePayload as unknown as PayloadRenderer,
  RuleEngineEffectCreateGroupPayload:
    RuleEngineEffectCreateGroupPayload as unknown as PayloadRenderer,
  RuleEngineEffectEliminatePayload:
    RuleEngineEffectEliminatePayload as unknown as PayloadRenderer,
  RuleEngineEffectEmitSeedingPinPayload:
    RuleEngineEffectEmitSeedingPinPayload as unknown as PayloadRenderer,
  RuleEngineEffectEndStepPayload:
    RuleEngineEffectEndStepPayload as unknown as PayloadRenderer,
  RuleEngineEffectSetMetadataPayload:
    RuleEngineEffectSetMetadataPayload as unknown as PayloadRenderer,
  RuleEngineGenerationScriptRanPayload:
    RuleEngineGenerationScriptRanPayload as unknown as PayloadRenderer,
  RuleEngineManualOverridePayload:
    RuleEngineManualOverridePayload as unknown as PayloadRenderer,
  TournamentsStepGeneratedPayload:
    TournamentsStepGeneratedPayload as unknown as PayloadRenderer,
  TournamentsStepResetPayload:
    TournamentsStepResetPayload as unknown as PayloadRenderer,
};

function isKnownPayloadTypename(
  value: string,
): value is JournalPayloadTypename {
  return value in PAYLOAD_RENDERERS;
}

export function JournalPayloadRenderer({ payload }: Props) {
  if (!isKnownPayloadTypename(payload.__typename)) {
    return (
      <Text size="xs" c="dimmed">
        Unrecognized payload type: {payload.__typename}
      </Text>
    );
  }
  const Component = PAYLOAD_RENDERERS[payload.__typename];
  return <Component data={payload} />;
}
